import { TransactionEntity } from './TransactionEntity';

export class TransactionItem {
    id?: number;
    record_id?: number;
    dateTime?: string;
    action?: TransactionEntity;
    direct_object?: TransactionEntity;
    indirect_object?: TransactionEntity;
    qty?: number;
    paymentMedia?: TransactionEntity;
    amount?: number;
    created_by_user_id?: number; // User who created the transaction
    private static readonly ID_PREFIX = 'txn_';
    private static readonly ID_TAG = 'balancio';

    constructor(init?: Partial<TransactionItem>) {
        Object.assign(this, init);
    }

    /**
     * Encodes the ID with a tag to create a user-friendly, lowercase string of a consistent length.
     * Note: In browser environment, we use btoa for Base64 encoding
     * @returns A Base64 encoded lowercase string.
     */
    getHashedId(): string {
        if (!this.id) return '';
        
        const stringToEncode = this.id + TransactionItem.ID_TAG;
        
        // Use btoa for Base64 encoding in browser environment
        let encodedId: string;
        try {
            // Convert to Base64 and make URL-safe
            encodedId = btoa(stringToEncode)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=+$/, ''); // Remove padding
        } catch (e) {
            console.error('Error encoding ID:', e);
            return '';
        }
        
        // Return as a lowercase string with the prefix
        return TransactionItem.ID_PREFIX + encodedId.toLowerCase();
    }

    /**
     * Decodes a user-friendly string back to the original numeric ID.
     * @param friendlyId The encoded lowercase string.
     * @returns The original numeric ID.
     * @throws Error if the ID cannot be decoded.
     */
    static getIdFromHash(friendlyId: string): number {
        if (!friendlyId || !friendlyId.toLowerCase().startsWith(TransactionItem.ID_PREFIX)) {
            throw new Error(`Invalid ID format. Must start with '${TransactionItem.ID_PREFIX}'.`);
        }

        // Remove the "txn_" prefix
        const encodedPart = friendlyId.substring(TransactionItem.ID_PREFIX.toString().length);

        try {
            // Add padding back if needed and convert from URL-safe Base64
            let base64 = encodedPart
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            
            // Add padding
            while (base64.length % 4) {
                base64 += '=';
            }

            // Decode from Base64
            const decodedString = atob(base64);

            // Check if the decoded string contains our tag
            if (!decodedString.endsWith(TransactionItem.ID_TAG)) {
                throw new Error('ID tag mismatch. The ID is malformed.');
            }

            // Remove the tag from the end to get the original ID string
            const idString = decodedString.substring(0, decodedString.length - TransactionItem.ID_TAG.length);
            return parseInt(idString, 10);

        } catch (e) {
            console.error('Error decoding Transaction ID:', e);
            throw new Error('Invalid Transaction ID format');
        }
    }
}